import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { AlertTriangle, ArrowLeft, ChevronDown, HelpCircle, Plus, Save, X } from 'lucide-react';

import NavigationBar from '../components/NavigationBar';
import BusinessDashboardSidebar from '../components/BusinessDashboardSidebar';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../hooks/useAuth';
import { useBusiness } from '../contexts/BusinessContext';
import apiService from '../services/api';
import { Appointment } from '../types';
import { getAppointmentStartIso } from '../utils/appointmentHelpers';
import { shouldShowBusinessDashboard } from '../utils/navigationUtils';
import { fuzzyMatch, searchServices } from '../utils/searchUtils';
import { isBusinessOwner } from '../utils/roleUtils';

type DraftAssignment = {
  clientId: string; // local-only ID for React keys
  id?: string; // backend assignment UUID (only for existing assignments)
  serviceId: string;
  serviceName: string;
  durationMinutes: number;
  price: number;
  serviceQuotable?: boolean;
  notes?: string;
  insistOnProfessional?: boolean;
  professionalId?: string;
  /** UTC ISO instants (timestamptz) */
  fromDateTime?: string;
  toDateTime?: string;
};

const getProfessionalDisplayName = (p: any): string => {
  const fullName = `${p?.firstName || ''} ${p?.lastName || ''}`.trim();
  return fullName || p?.email || 'Professional';
};

const toLocalDateTimeInputValue = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  // datetime-local expects local time "YYYY-MM-DDTHH:mm"
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fromLocalDateTimeInputToIso = (value: string): string | undefined => {
  const v = (value || '').trim();
  if (!v) return undefined;
  const d = new Date(v); // interpreted as local time
  if (!Number.isFinite(d.getTime())) return undefined;
  return d.toISOString();
};

const BusinessEditAppointmentDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const { user } = useAuth();
  const { currentBusiness } = useBusiness();

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const [appointment, setAppointment] = useState<Appointment | null>(null);
  const [draftAssignments, setDraftAssignments] = useState<DraftAssignment[]>([]);

  const [businessServices, setBusinessServices] = useState<any[]>([]);
  const [professionals, setProfessionals] = useState<any[]>([]);
  const [capabilitiesByProfessional, setCapabilitiesByProfessional] = useState<Record<string, any[]>>({});
  const [schedulesByProfessional, setSchedulesByProfessional] = useState<Record<string, any[]>>({});
  const [existingAppointmentsByProfessional, setExistingAppointmentsByProfessional] = useState<Record<string, Appointment[]>>({});

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmSaveWithNoServicesOpen, setConfirmSaveWithNoServicesOpen] = useState(false);
  const [addingServiceId, setAddingServiceId] = useState('');
  const [addingProfessionalId, setAddingProfessionalId] = useState('');
  const [serviceSearchQuery, setServiceSearchQuery] = useState('');
  const [isServiceDropdownOpen, setIsServiceDropdownOpen] = useState(false);
  const [serviceDropdownPlacement, setServiceDropdownPlacement] = useState<'bottom' | 'top'>('bottom');
  const [serviceDropdownMaxHeight, setServiceDropdownMaxHeight] = useState<number>(288); // 18rem
  const serviceDropdownRef = React.useRef<HTMLDivElement>(null);

  const [professionalSearchQuery, setProfessionalSearchQuery] = useState('');
  const [isProfessionalDropdownOpen, setIsProfessionalDropdownOpen] = useState(false);
  const [professionalDropdownPlacement, setProfessionalDropdownPlacement] = useState<'bottom' | 'top'>('bottom');
  const [professionalDropdownMaxHeight, setProfessionalDropdownMaxHeight] = useState<number>(288); // 18rem
  const professionalDropdownRef = React.useRef<HTMLDivElement>(null);

  const toggleSidebar = () => setIsSidebarCollapsed(!isSidebarCollapsed);

  const userIsOwner = useMemo(() => isBusinessOwner(user || null), [user]);

  const loadData = useCallback(async () => {
    if (!appointmentId) {
      toast.error('Appointment ID is missing');
      return;
    }
    if (!currentBusiness?.id) {
      toast.error('No business selected');
      return;
    }

    setLoading(true);
    try {
      const apt = await apiService.getAppointment(appointmentId);
      setAppointment(apt);

      setDraftAssignments(
        (apt.assignments || []).map((a) => ({
          clientId: a.id,
          id: a.id,
          serviceId: a.serviceId,
          serviceName: a.serviceName,
          durationMinutes: a.durationMinutes,
          price: typeof a.price === 'number' ? a.price : (a.price != null ? Number(a.price) : 0),
          serviceQuotable: (a as any).serviceQuotable === true,
          notes: a.notes,
          insistOnProfessional: a.insistOnProfessional,
          professionalId: a.professionalId,
          fromDateTime: (a as any).fromDateTime,
          toDateTime: (a as any).toDateTime
        }))
      );

      const [services, profs] = await Promise.all([
        apiService.getBusinessServices(currentBusiness.id),
        apiService.getBusinessProfessionalsDetailed(currentBusiness.id)
      ]);

      setBusinessServices(services || []);

      const formattedProfs = (profs || []).map((prof: any) => ({
        id: prof.id,
        firstName: prof.name?.split(' ')[0] || prof.firstName || '',
        lastName: prof.name?.split(' ').slice(1).join(' ') || prof.lastName || '',
        email: prof.email,
        phone: prof.phone,
        canPerformServices: prof.canPerformServices
      }));
      setProfessionals(formattedProfs);

      // Load capability + schedule + existing appointments for warning display
      const capabilityPromises = formattedProfs.map(async (professional: any) => {
        try {
          const caps = await apiService.getProfessionalServices(currentBusiness.id, professional.id);
          return { professionalId: professional.id, capabilities: caps || [] };
        } catch {
          return { professionalId: professional.id, capabilities: [] };
        }
      });

      const schedulePromises = formattedProfs.map(async (professional: any) => {
        try {
          const schedules = await apiService.getProfessionalAvailability(currentBusiness.id, professional.id);
          return { professionalId: professional.id, schedules: schedules || [] };
        } catch {
          return { professionalId: professional.id, schedules: [] };
        }
      });

      const aptStartIso = getAppointmentStartIso(apt);
      const appointmentDate = aptStartIso ? new Date(aptStartIso) : new Date();
      const year = appointmentDate.getFullYear();
      const month = String(appointmentDate.getMonth() + 1).padStart(2, '0');
      const day = String(appointmentDate.getDate()).padStart(2, '0');
      const appointmentDateStr = `${year}-${month}-${day}T00:00:00`;
      const appointmentDateEndStr = `${year}-${month}-${day}T23:59:59`;

      const appointmentPromises = formattedProfs.map(async (professional: any) => {
        try {
          const result = await apiService.getAppointments({
            businessId: currentBusiness.id,
            professionalId: professional.id,
            startDate: appointmentDateStr,
            endDate: appointmentDateEndStr,
            status: 'upcoming'
          });
          return { professionalId: professional.id, appointments: result.data || [] };
        } catch {
          return { professionalId: professional.id, appointments: [] };
        }
      });

      const [capabilityResults, scheduleResults, appointmentResults] = await Promise.all([
        Promise.all(capabilityPromises),
        Promise.all(schedulePromises),
        Promise.all(appointmentPromises)
      ]);

      const capsMap: Record<string, any[]> = {};
      capabilityResults.forEach((r) => (capsMap[r.professionalId] = r.capabilities));
      setCapabilitiesByProfessional(capsMap);

      const schedMap: Record<string, any[]> = {};
      scheduleResults.forEach((r) => (schedMap[r.professionalId] = r.schedules));
      setSchedulesByProfessional(schedMap);

      const apptMap: Record<string, Appointment[]> = {};
      appointmentResults.forEach((r) => (apptMap[r.professionalId] = r.appointments));
      setExistingAppointmentsByProfessional(apptMap);
    } catch (err: any) {
      console.error('Error loading appointment edit data:', err);
      toast.error(err.response?.data?.message || 'Failed to load appointment');
    } finally {
      setLoading(false);
    }
  }, [appointmentId, currentBusiness?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const recomputeServiceDropdownPlacement = useCallback(() => {
    const container = serviceDropdownRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const viewportHeight = window.innerHeight || 0;
    const padding = 12;
    const desiredMax = 288; // 18rem

    const spaceBelow = Math.max(0, viewportHeight - rect.bottom);
    const spaceAbove = Math.max(0, rect.top);

    // Flip to top if there's noticeably less space below, and more space above.
    const shouldFlipToTop = spaceBelow < 180 && spaceAbove > spaceBelow;
    const placement: 'bottom' | 'top' = shouldFlipToTop ? 'top' : 'bottom';

    const available = placement === 'bottom' ? spaceBelow : spaceAbove;
    const maxHeight = Math.max(120, Math.min(desiredMax, available - padding));

    setServiceDropdownPlacement(placement);
    setServiceDropdownMaxHeight(maxHeight);
  }, []);

  const recomputeProfessionalDropdownPlacement = useCallback(() => {
    const container = professionalDropdownRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const viewportHeight = window.innerHeight || 0;
    const padding = 12;
    const desiredMax = 288; // 18rem

    const spaceBelow = Math.max(0, viewportHeight - rect.bottom);
    const spaceAbove = Math.max(0, rect.top);

    const shouldFlipToTop = spaceBelow < 180 && spaceAbove > spaceBelow;
    const placement: 'bottom' | 'top' = shouldFlipToTop ? 'top' : 'bottom';

    const available = placement === 'bottom' ? spaceBelow : spaceAbove;
    const maxHeight = Math.max(120, Math.min(desiredMax, available - padding));

    setProfessionalDropdownPlacement(placement);
    setProfessionalDropdownMaxHeight(maxHeight);
  }, []);

  useEffect(() => {
    if (!isServiceDropdownOpen) return;

    // Measure after render
    const raf = requestAnimationFrame(() => {
      recomputeServiceDropdownPlacement();
    });

    const handle = () => recomputeServiceDropdownPlacement();
    window.addEventListener('resize', handle);
    // Capture scroll events from any scroll container
    window.addEventListener('scroll', handle, true);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', handle);
      window.removeEventListener('scroll', handle, true);
    };
  }, [isServiceDropdownOpen, recomputeServiceDropdownPlacement]);

  useEffect(() => {
    if (!isProfessionalDropdownOpen) return;

    const raf = requestAnimationFrame(() => {
      recomputeProfessionalDropdownPlacement();
    });

    const handle = () => recomputeProfessionalDropdownPlacement();
    window.addEventListener('resize', handle);
    window.addEventListener('scroll', handle, true);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', handle);
      window.removeEventListener('scroll', handle, true);
    };
  }, [isProfessionalDropdownOpen, recomputeProfessionalDropdownPlacement]);

  // Close service dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (serviceDropdownRef.current && !serviceDropdownRef.current.contains(target)) {
        setIsServiceDropdownOpen(false);
      }
      if (professionalDropdownRef.current && !professionalDropdownRef.current.contains(target)) {
        setIsProfessionalDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Duplicates are allowed (same service can appear multiple times), so we do not de-dupe by serviceId.

  const getServiceCategoryName = useCallback((service: any): string | null => {
    if (!service) return null;
    const cat = service.category;
    const categoryName =
      cat?.name ||
      cat?.category_name ||
      cat?.categoryName ||
      service.categoryName ||
      service.category_name;
    if (categoryName && typeof categoryName === 'string') return categoryName;
    if (typeof cat === 'string' && cat.trim()) return cat;
    return null;
  }, []);

  const normalizeServiceForSearch = useCallback(
    (service: any) => {
      const categoryName = getServiceCategoryName(service);
      const priceNum = Number(service?.price ?? 0) || 0;
      const durationNum =
        Number(service?.durationMinutes ?? service?.duration_minutes ?? service?.duration ?? service?.duration_min ?? 0) || 0;
      const categoryObj = service?.category
        ? service.category
        : categoryName
        ? { name: categoryName }
        : undefined;
      return {
        ...service,
        price: priceNum,
        durationMinutes: durationNum,
        category: categoryObj
      };
    },
    [getServiceCategoryName]
  );

  const searchableBusinessServices = useMemo(
    () => (businessServices || []).map((s) => normalizeServiceForSearch(s)),
    [businessServices, normalizeServiceForSearch]
  );

  const filteredBusinessServices = useMemo(() => {
    const searched = serviceSearchQuery.trim()
      ? searchServices(searchableBusinessServices as any, serviceSearchQuery)
      : searchableBusinessServices;

    // Sort: add-ons go to the end (same behavior as booking flow)
    return [...searched].sort((a: any, b: any) => {
      const aIsAddOn = a.isAddOn || a.is_add_on;
      const bIsAddOn = b.isAddOn || b.is_add_on;
      if (aIsAddOn && !bIsAddOn) return 1;
      if (!aIsAddOn && bIsAddOn) return -1;
      return 0;
    });
  }, [searchableBusinessServices, serviceSearchQuery]);

  const hasServiceCapability = useCallback(
    (professionalId: string, serviceId: string): boolean => {
      const caps = capabilitiesByProfessional[professionalId] || [];
      return caps.some((cap: any) => String(cap.id || cap.serviceId || cap.service_id) === String(serviceId));
    },
    [capabilitiesByProfessional]
  );

  const hasScheduleConflict = useCallback(
    (professionalId: string, startTime: Date, durationMinutes: number): boolean => {
      if (!appointment) return false;

      const schedules = schedulesByProfessional[professionalId] || [];
      if (schedules.length === 0) return true; // No schedule = conflict warning

      const dayOfWeek = startTime.getDay();
      const appointmentTime = startTime.toTimeString().slice(0, 5);
      const daySchedule = schedules.find((schedule: any) => {
        const scheduleDay = schedule.dayOfWeek !== undefined ? schedule.dayOfWeek : schedule.day_of_week;
        return scheduleDay === dayOfWeek;
      });

      if (!daySchedule || !daySchedule.isAvailable) return true;

      const start = daySchedule.startTime || daySchedule.start_time || '09:00';
      const end = daySchedule.endTime || daySchedule.end_time || '17:00';

      const normalizeTime = (time: string) => {
        const [hours, minutes] = time.split(':').map(Number);
        return hours * 60 + minutes;
      };

      const normalizedAppointmentTime = normalizeTime(appointmentTime);
      const normalizedStartTime = normalizeTime(start);
      const normalizedEndTime = normalizeTime(end);
      const appointmentEndTime = normalizedAppointmentTime + durationMinutes;

      if (normalizedAppointmentTime < normalizedStartTime) return true;
      if (appointmentEndTime > normalizedEndTime) return true;

      const endTime = new Date(startTime.getTime() + durationMinutes * 60 * 1000);

      const existingAppointments = existingAppointmentsByProfessional[professionalId] || [];
      const otherAppointments = existingAppointments.filter((apt) => apt.id !== appointment.id);

      for (const existingApt of otherAppointments) {
        const existingStartIso = getAppointmentStartIso(existingApt);
        if (!existingStartIso) continue;
        const existingStart = new Date(existingStartIso);
        const existingDuration =
          existingApt.assignments?.reduce((total: number, a: any) => total + (a.durationMinutes || 0), 0) || 0;
        const existingEnd = new Date(existingStart.getTime() + existingDuration * 60 * 1000);

        if (
          (startTime >= existingStart && startTime < existingEnd) ||
          (endTime > existingStart && endTime <= existingEnd) ||
          (startTime <= existingStart && endTime >= existingEnd)
        ) {
          return true;
        }
      }

      return false;
    },
    [appointment, schedulesByProfessional, existingAppointmentsByProfessional]
  );

  const getEffectiveStartTimeForRow = useCallback(
    (rowIndex: number, professionalId: string): Date => {
      const baseIso = getAppointmentStartIso(appointment!);
      const base = baseIso ? new Date(baseIso) : new Date();
      let offsetMinutes = 0;
      for (let i = 0; i < rowIndex; i++) {
        const a = draftAssignments[i];
        if (a.professionalId && String(a.professionalId) === String(professionalId)) {
          offsetMinutes += a.durationMinutes || 0;
        }
      }
      return new Date(base.getTime() + offsetMinutes * 60 * 1000);
    },
    [appointment, draftAssignments]
  );

  const getWarningsForSelection = useCallback(
    (rowIndex: number, serviceId: string, durationMinutes: number, professionalId?: string) => {
      if (!professionalId) return [];
      const warnings: string[] = [];
      if (!hasServiceCapability(professionalId, serviceId)) warnings.push('No capability');
      const start = getEffectiveStartTimeForRow(rowIndex, professionalId);
      if (hasScheduleConflict(professionalId, start, durationMinutes)) warnings.push('Schedule conflict');
      return warnings;
    },
    [getEffectiveStartTimeForRow, hasScheduleConflict, hasServiceCapability]
  );

  const ProfessionalSearchDropdown: React.FC<{
    rowIndex: number;
    assignment: DraftAssignment;
    disabled?: boolean;
    value?: string;
    onChange: (nextProfessionalId?: string) => void;
  }> = ({ rowIndex, assignment, disabled, value, onChange }) => {
    const [open, setOpen] = useState(false);
    const [placement, setPlacement] = useState<'bottom' | 'top'>('bottom');
    const [maxHeight, setMaxHeight] = useState<number>(288);
    const ref = React.useRef<HTMLDivElement>(null);

    const recomputePlacement = useCallback(() => {
      const container = ref.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const viewportHeight = window.innerHeight || 0;
      const padding = 12;
      const desiredMax = 288;
      const spaceBelow = Math.max(0, viewportHeight - rect.bottom);
      const spaceAbove = Math.max(0, rect.top);
      const shouldFlipToTop = spaceBelow < 180 && spaceAbove > spaceBelow;
      const nextPlacement: 'bottom' | 'top' = shouldFlipToTop ? 'top' : 'bottom';
      const available = nextPlacement === 'bottom' ? spaceBelow : spaceAbove;
      const nextMax = Math.max(120, Math.min(desiredMax, available - padding));
      setPlacement(nextPlacement);
      setMaxHeight(nextMax);
    }, []);

    useEffect(() => {
      if (!open) return;
      const raf = requestAnimationFrame(() => recomputePlacement());
      const handle = () => recomputePlacement();
      window.addEventListener('resize', handle);
      window.addEventListener('scroll', handle, true);
      return () => {
        cancelAnimationFrame(raf);
        window.removeEventListener('resize', handle);
        window.removeEventListener('scroll', handle, true);
      };
    }, [open, recomputePlacement]);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Node;
        if (ref.current && !ref.current.contains(target)) {
          setOpen(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const selectedWarnings = useMemo(() => {
      if (!value) return [];
      return getWarningsForSelection(rowIndex, assignment.serviceId, assignment.durationMinutes, value);
    }, [assignment.durationMinutes, assignment.serviceId, rowIndex, value, getWarningsForSelection]);

    const inputDisabled = !!disabled;
    const selectedProfessional = value
      ? professionals.find((x: any) => String(x.id) === String(value))
      : null;
    const selectedLabel = value ? getProfessionalDisplayName(selectedProfessional) : 'No assignment';

    return (
      <div className="relative" ref={ref}>
        <button
          type="button"
          onClick={() => {
            if (inputDisabled) return;
            setOpen((v) => !v);
          }}
          className="w-full px-3 py-2 pr-16 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-left"
          disabled={inputDisabled}
        >
          <span className="block truncate font-semibold text-gray-900">{selectedLabel || 'No assignment'}</span>
        </button>

        {selectedWarnings.length > 0 && (
          <div
            className="absolute right-9 top-1/2 -translate-y-1/2 text-yellow-700"
            title={selectedWarnings.join(', ')}
          >
            <AlertTriangle className="w-4 h-4" />
          </div>
        )}

        <button
          type="button"
          aria-label="Toggle professional dropdown"
          onClick={() => {
            if (inputDisabled) return;
            setOpen((v) => !v);
          }}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
          disabled={inputDisabled}
        >
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>

        {open && (
          <div
            className={`absolute left-0 right-0 bg-white border border-gray-300 rounded-lg shadow-lg z-30 overflow-auto ${
              placement === 'bottom' ? 'top-full mt-2' : 'bottom-full mb-2'
            }`}
            style={{ maxHeight }}
          >
            <button
              type="button"
              onClick={() => {
                onChange(undefined);
                setOpen(false);
              }}
              className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors text-sm"
            >
              No assignment
            </button>
            <div className="h-px bg-gray-200" />
            {professionals.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-600">No professionals available</div>
            ) : (
              professionals.slice(0, 100).map((p: any) => {
                const warnings = getWarningsForSelection(
                  rowIndex,
                  assignment.serviceId,
                  assignment.durationMinutes,
                  p.id
                );
                const warningText = warnings.join(', ');
                const displayName = getProfessionalDisplayName(p);
                const isSelected = value && String(value) === String(p.id);

                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => {
                      onChange(String(p.id));
                      setOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                      isSelected ? 'bg-gray-50' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-semibold text-gray-900 truncate flex items-center gap-2">
                          <span className="truncate">{displayName}</span>
                          {warnings.length > 0 && (
                            <span className="text-xs font-semibold text-yellow-800 bg-yellow-50 border border-yellow-200 rounded-full px-2 py-0.5 flex-shrink-0">
                              ⚠
                            </span>
                          )}
                        </div>
                        {p.email && <div className="text-xs text-gray-600 mt-1 truncate">{p.email}</div>}
                      </div>
                      {warnings.length > 0 && (
                        <div className="text-yellow-700 flex-shrink-0" title={warningText}>
                          <AlertTriangle className="w-4 h-4" />
                        </div>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>
    );
  };

  const filteredProfessionals = useMemo(() => {
    const query = professionalSearchQuery.trim();
    if (!query) return professionals;

    const scored: Array<{ p: any; score: number }> = [];
    for (const p of professionals) {
      const fullName = `${p.firstName || ''} ${p.lastName || ''}`.trim();
      const email = p.email || '';
      const nameScore = fuzzyMatch(query, fullName) ?? 0;
      const emailScore = fuzzyMatch(query, email) ?? 0;
      const best = Math.max(nameScore, emailScore);
      if (best > 0) {
        scored.push({ p, score: best });
      }
    }
    scored.sort((a, b) => b.score - a.score);
    return scored.map((x) => x.p);
  }, [professionals, professionalSearchQuery]);

  const canRemoveAssignment = useCallback(
    (assignment: DraftAssignment): boolean => {
      // Owners can remove anything
      if (userIsOwner) return true;
      // Non-owner professionals can only remove services assigned to themselves
      if (!user?.id) return false;
      return !!assignment.professionalId && String(assignment.professionalId) === String(user.id);
    },
    [user?.id, userIsOwner]
  );

  const handleRemoveService = (assignment: DraftAssignment) => {
    if (!canRemoveAssignment(assignment)) {
      return;
    }
    setDraftAssignments((prev) => prev.filter((a) => a.clientId !== assignment.clientId));
  };

  const handleAddService = () => {
    if (!addingServiceId) return;
    const svc = businessServices.find((s: any) => String(s.id) === String(addingServiceId));
    if (!svc) return;

    const durationMinutes =
      Number(svc.durationMinutes ?? svc.duration_minutes ?? svc.duration ?? svc.duration_min ?? 0) || 0;
    if (!durationMinutes || durationMinutes < 1) {
      toast.error('This service is missing a valid duration');
      return;
    }

    const isQuotable = (svc as any)?.quotable === true;
    const price = isQuotable ? 0 : (Number(svc.price ?? 0) || 0);
    const baseStart = new Date(getAppointmentStartIso(appointment) || new Date().toISOString());
    const offsetMinutes = draftAssignments.reduce((sum, row) => sum + (Number(row.durationMinutes) || 0), 0);
    const from = new Date(baseStart.getTime() + offsetMinutes * 60 * 1000);
    const to = new Date(from.getTime() + durationMinutes * 60 * 1000);

    setDraftAssignments((prev) => [
      ...prev,
      {
        clientId: `new-${svc.id}-${Date.now()}`,
        serviceId: String(svc.id),
        serviceName: svc.name || 'Service',
        durationMinutes,
        price,
        serviceQuotable: isQuotable,
        professionalId: addingProfessionalId || undefined,
        notes: '',
        insistOnProfessional: false,
        fromDateTime: from.toISOString(),
        toDateTime: to.toISOString()
      }
    ]);

    setAddingServiceId('');
    setAddingProfessionalId('');
    setServiceSearchQuery('');
    setProfessionalSearchQuery('');
    setIsServiceDropdownOpen(false);
    setIsProfessionalDropdownOpen(false);
  };

  const performSave = async () => {
    if (!appointment) return;
    setSaving(true);
    try {
      const payloadAssignments = draftAssignments.map((a) => ({
        ...(a.id ? { id: a.id } : {}),
        serviceId: a.serviceId,
        professionalId: a.professionalId || undefined,
        durationMinutes: a.durationMinutes,
        price: a.serviceQuotable && (a.price === 0 || a.price == null) ? null : a.price,
        notes: a.notes,
        insistOnProfessional: a.insistOnProfessional === true,
        fromDateTime: a.fromDateTime,
        toDateTime: a.toDateTime
      }));

      await apiService.updateAppointment(appointment.id, {
        assignments: payloadAssignments as any,
        allowOverride: true
      } as any);

      toast.success('Appointment updated');
      navigate(`/appointments/${appointment.id}${location.search || ''}`);
    } catch (err: any) {
      console.error('Error saving appointment edits:', err);
      toast.error(err.response?.data?.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    // Allow saving with zero services, but confirm with the user first.
    if (draftAssignments.length === 0) {
      setConfirmSaveWithNoServicesOpen(true);
      return;
    }
    await performSave();
  };

  if (loading || !appointment) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <NavigationBar />

      <div className="flex-1 lg:grid lg:grid-cols-[auto_1fr] lg:min-h-screen">
        {shouldShowBusinessDashboard(user!) && (
          <BusinessDashboardSidebar
            isSidebarOpen={isSidebarOpen}
            isSidebarCollapsed={isSidebarCollapsed}
            activeSection="appointments"
            onClose={() => setIsSidebarOpen(false)}
            onToggleCollapse={toggleSidebar}
            unreadNotificationCount={0}
          />
        )}

        <div className="flex flex-col min-h-screen">
          <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3">
            <div className="flex items-center justify-between">
              <button onClick={() => setIsSidebarOpen(true)} className="p-2 rounded-md hover:bg-gray-100">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <h1 className="text-lg font-semibold text-gray-900">Edit Appointment Details</h1>
              <div className="w-10"></div>
            </div>
          </div>

          <div className="flex-1 p-4 md:p-8">
            <button
              onClick={() => navigate(`/appointments/${appointment.id}${location.search || ''}`)}
              className="text-gray-600 hover:text-gray-900 mb-4 flex items-center gap-2"
              disabled={saving}
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>

            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Edit Appointment Details</h1>
                <div className="text-sm text-gray-600 mt-1">
                  You can add/remove services and assign professionals. If a professional has conflicts or lacks capability,
                  you’ll see a warning but you can still proceed.
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Services</h3>

                <div className="space-y-3">
                  {draftAssignments.map((a, idx) => {
                    const warnings = getWarningsForSelection(idx, a.serviceId, a.durationMinutes, a.professionalId);
                    const removeAllowed = canRemoveAssignment(a);
                    const removeDisabled =
                      saving || !removeAllowed;
                    const removeTitle = saving
                      ? 'Saving...'
                      : removeAllowed
                      ? 'Remove service'
                      : 'Only the assigned professional can remove this service (owners can remove any)';
                    return (
                      <div
                        key={a.clientId}
                        className="border border-gray-200 rounded-lg p-4 bg-white flex flex-col gap-3"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="font-semibold text-gray-900 truncate">{a.serviceName}</div>
                            <div className="text-sm text-gray-600 flex items-center gap-1">
                              {a.durationMinutes} min •
                              {a.serviceQuotable === true && (a.price === 0 || a.price == null) ? (
                                <span
                                  className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-gray-700"
                                  title="Quotable price"
                                >
                                  <HelpCircle className="w-4 h-4" />
                                </span>
                              ) : (
                                <span className="font-mono">${Number(a.price || 0).toFixed(2)}</span>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => handleRemoveService(a)}
                            className={`flex-shrink-0 transition-all p-1.5 rounded-lg ${
                              removeDisabled
                                ? 'text-gray-300 cursor-not-allowed'
                                : 'text-red-600 hover:text-red-700 hover:bg-red-50'
                            }`}
                            disabled={removeDisabled}
                            title={removeTitle}
                            aria-label={`Remove ${a.serviceName}`}
                            type="button"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Assigned Professional</label>
                          <ProfessionalSearchDropdown
                            rowIndex={idx}
                            assignment={a}
                            disabled={saving}
                            value={a.professionalId}
                            onChange={(nextProfessionalId) => {
                              setDraftAssignments((prev) =>
                                prev.map((row) =>
                                  row.clientId === a.clientId ? { ...row, professionalId: nextProfessionalId } : row
                                )
                              );
                            }}
                          />

                          {warnings.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2 text-xs">
                              {warnings.map((w) => (
                                <div key={w} className="flex items-center gap-1 text-yellow-700">
                                  <AlertTriangle className="w-3 h-3" />
                                  <span>{w}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">From Date/Time</label>
                            <input
                              type="datetime-local"
                              value={toLocalDateTimeInputValue(a.fromDateTime)}
                              onChange={(e) => {
                                const iso = fromLocalDateTimeInputToIso(e.target.value);
                                setDraftAssignments((prev) =>
                                  prev.map((row) =>
                                    row.clientId === a.clientId ? { ...row, fromDateTime: iso } : row
                                  )
                                );
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              disabled={saving}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">To Date/Time</label>
                            <input
                              type="datetime-local"
                              value={toLocalDateTimeInputValue(a.toDateTime)}
                              onChange={(e) => {
                                const iso = fromLocalDateTimeInputToIso(e.target.value);
                                setDraftAssignments((prev) =>
                                  prev.map((row) =>
                                    row.clientId === a.clientId ? { ...row, toDateTime: iso } : row
                                  )
                                );
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              disabled={saving}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="h-px bg-gray-200 my-4"></div>

                <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-start">
                  <div className="flex-1 w-full">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Add service</label>
                    <div className="relative" ref={serviceDropdownRef}>
                      <input
                        value={serviceSearchQuery}
                        onChange={(e) => {
                          setServiceSearchQuery(e.target.value);
                          setIsServiceDropdownOpen(true);
                          // If user starts typing, clear previously selected service id (forces explicit selection)
                          setAddingServiceId('');
                          setAddingProfessionalId('');
                          setProfessionalSearchQuery('');
                        }}
                        onFocus={() => setIsServiceDropdownOpen(true)}
                        placeholder="Search services by name, price, category, add-ons..."
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        disabled={saving}
                      />
                      <button
                        type="button"
                        aria-label="Toggle service dropdown"
                        onClick={() => {
                          if (saving) return;
                          setIsServiceDropdownOpen((v) => !v);
                        }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        disabled={saving}
                      >
                        <ChevronDown className={`w-4 h-4 transition-transform ${isServiceDropdownOpen ? 'rotate-180' : ''}`} />
                      </button>

                      {isServiceDropdownOpen && (
                        <div
                          className={`absolute left-0 right-0 bg-white border border-gray-300 rounded-lg shadow-lg z-30 overflow-auto ${
                            serviceDropdownPlacement === 'bottom' ? 'top-full mt-2' : 'bottom-full mb-2'
                          }`}
                          style={{ maxHeight: serviceDropdownMaxHeight }}
                        >
                          {filteredBusinessServices.length === 0 ? (
                            <div className="px-4 py-3 text-sm text-gray-600">No matching services</div>
                          ) : (
                            filteredBusinessServices.slice(0, 100).map((s: any) => {
                              const categoryName = getServiceCategoryName(s);
                              const isAddOn = s.isAddOn || s.is_add_on;
                              const duration =
                                Number(s.durationMinutes ?? s.duration_minutes ?? s.duration ?? s.duration_min ?? 0) || 0;
                              const price = Number(s.price ?? 0) || 0;
                              const alreadyAdded = false;

                              return (
                                <button
                                  key={s.id}
                                  type="button"
                                  onClick={() => {
                                    setAddingServiceId(String(s.id));
                                    setServiceSearchQuery(String(s.name || 'Service'));
                                    setIsServiceDropdownOpen(false);
                                    setAddingProfessionalId('');
                                    setProfessionalSearchQuery('');
                                  }}
                                  className={`w-full text-left px-4 py-3 transition-colors ${
                                    'hover:bg-gray-50'
                                  }`}
                                >
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                      <div className="font-semibold text-gray-900 truncate flex items-center gap-2">
                                        <span className="truncate">{s.name}</span>
                                        {isAddOn && (
                                          <span className="text-xs font-semibold text-purple-700 bg-purple-50 border border-purple-200 rounded-full px-2 py-0.5 flex-shrink-0">
                                            Add-on
                                          </span>
                                        )}
                                      </div>
                                      <div className="text-xs text-gray-600 mt-1 flex flex-wrap gap-x-3 gap-y-1">
                                        {categoryName && (
                                          <span className="truncate">
                                            <span className="font-medium">Category:</span> {categoryName}
                                          </span>
                                        )}
                                        <span>
                                          <span className="font-medium">Duration:</span> {duration} min
                                        </span>
                                      </div>
                                    </div>
                                    <div className="text-sm font-mono text-gray-900 flex-shrink-0">
                                      ${price.toFixed(2)}
                                    </div>
                                  </div>
                                </button>
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      You can add the same service multiple times.
                    </div>
                  </div>

                  <div className="w-full sm:w-[280px]">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Assign professional</label>
                    <div className="relative" ref={professionalDropdownRef}>
                      <input
                        value={professionalSearchQuery}
                        onChange={(e) => {
                          setProfessionalSearchQuery(e.target.value);
                          setIsProfessionalDropdownOpen(true);
                          // Force explicit selection
                          setAddingProfessionalId('');
                        }}
                        onFocus={() => setIsProfessionalDropdownOpen(true)}
                        placeholder={!addingServiceId ? 'Select a service first' : 'Search professionals by name...'}
                        className="w-full px-3 py-2 pr-16 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        disabled={saving || !addingServiceId}
                      />

                      {/* Selected-professional warning icon */}
                      {(() => {
                        if (!addingServiceId || !addingProfessionalId) return null;
                        const svc = businessServices.find((s: any) => String(s.id) === String(addingServiceId));
                        const durationMinutes =
                          Number(svc?.durationMinutes ?? svc?.duration_minutes ?? svc?.duration ?? svc?.duration_min ?? 0) || 0;
                        const warnings =
                          durationMinutes > 0
                            ? getWarningsForSelection(draftAssignments.length, String(addingServiceId), durationMinutes, addingProfessionalId)
                            : [];
                        if (warnings.length === 0) return null;

                        return (
                          <div
                            className="absolute right-9 top-1/2 -translate-y-1/2 text-yellow-700"
                            title={warnings.join(', ')}
                          >
                            <AlertTriangle className="w-4 h-4" />
                          </div>
                        );
                      })()}

                      <button
                        type="button"
                        aria-label="Toggle professional dropdown"
                        onClick={() => {
                          if (saving || !addingServiceId) return;
                          setIsProfessionalDropdownOpen((v) => !v);
                        }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        disabled={saving || !addingServiceId}
                      >
                        <ChevronDown
                          className={`w-4 h-4 transition-transform ${isProfessionalDropdownOpen ? 'rotate-180' : ''}`}
                        />
                      </button>

                      {isProfessionalDropdownOpen && (
                        <div
                          className={`absolute left-0 right-0 bg-white border border-gray-300 rounded-lg shadow-lg z-30 overflow-auto ${
                            professionalDropdownPlacement === 'bottom' ? 'top-full mt-2' : 'bottom-full mb-2'
                          }`}
                          style={{ maxHeight: professionalDropdownMaxHeight }}
                        >
                          {filteredProfessionals.length === 0 ? (
                            <div className="px-4 py-3 text-sm text-gray-600">No matching professionals</div>
                          ) : (
                            filteredProfessionals.slice(0, 100).map((p: any) => {
                              const svc = businessServices.find((s: any) => String(s.id) === String(addingServiceId));
                              const durationMinutes =
                                Number(svc?.durationMinutes ?? svc?.duration_minutes ?? svc?.duration ?? svc?.duration_min ?? 0) || 0;
                              const warnings =
                                addingServiceId && durationMinutes > 0
                                  ? getWarningsForSelection(draftAssignments.length, String(addingServiceId), durationMinutes, p.id)
                                  : [];
                              const warningText = warnings.join(', ');
                              const displayName = `${p.firstName || ''} ${p.lastName || ''}`.trim() || p.email || 'Professional';

                              return (
                                <button
                                  key={p.id}
                                  type="button"
                                  onClick={() => {
                                    setAddingProfessionalId(String(p.id));
                                    setProfessionalSearchQuery(displayName);
                                    setIsProfessionalDropdownOpen(false);
                                  }}
                                  className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors"
                                >
                                  <div className="flex items-center justify-between gap-3">
                                    <div className="min-w-0">
                                      <div className="font-semibold text-gray-900 truncate flex items-center gap-2">
                                        <span className="truncate">{displayName}</span>
                                        {warnings.length > 0 && (
                                          <span className="text-xs font-semibold text-yellow-800 bg-yellow-50 border border-yellow-200 rounded-full px-2 py-0.5 flex-shrink-0">
                                            ⚠
                                          </span>
                                        )}
                                      </div>
                                      {p.email && (
                                        <div className="text-xs text-gray-600 mt-1 truncate">{p.email}</div>
                                      )}
                                    </div>
                                    {warnings.length > 0 && (
                                      <div className="text-yellow-700 flex-shrink-0" title={warningText}>
                                        <AlertTriangle className="w-4 h-4" />
                                      </div>
                                    )}
                                  </div>
                                </button>
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      If ⚠ is shown, there may be capability/schedule warnings—but you can still assign and proceed.
                    </div>
                  </div>

                  <button
                    onClick={handleAddService}
                    disabled={saving || !addingServiceId || !addingProfessionalId}
                    className="aacore-btn2-secondary flex items-center gap-2 sm:self-start sm:mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Plus className="w-4 h-4" />
                    Add Service
                  </button>
                </div>

                {/* Mobile-only separator above action buttons */}
                <div className="h-px bg-gray-200 my-4 sm:hidden"></div>

                <div className="flex justify-end mt-5 gap-3">
                  <button
                    type="button"
                    onClick={() => navigate(`/appointments/${appointment.id}${location.search || ''}`)}
                    disabled={saving}
                    className="aacore-btn2-outline bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed flex-1 sm:flex-none justify-center flex items-center gap-2"
                  >
                    <X className="w-4 h-4" />
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving || draftAssignments.length === 0}
                    title={draftAssignments.length === 0 ? 'Add at least one service to enable saving' : undefined}
                    className="aacore-btn2-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed flex-1 sm:flex-none justify-center"
                  >
                    <Save className="w-4 h-4" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirm saving with no services */}
      {confirmSaveWithNoServicesOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Save with no services?</h2>
              <button
                onClick={() => setConfirmSaveWithNoServicesOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                disabled={saving}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              <p className="text-gray-700">
                You removed all services from this appointment. Saving will leave the appointment with <strong>no
                services</strong>. Do you want to continue?
              </p>
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setConfirmSaveWithNoServicesOpen(false)}
                disabled={saving}
                className="aacore-btn2-outline disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Go Back
              </button>
              <button
                type="button"
                onClick={async () => {
                  setConfirmSaveWithNoServicesOpen(false);
                  await performSave();
                }}
                disabled={saving}
                className="aacore-btn2-danger disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                Save Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessEditAppointmentDetailsPage;


